import type { CSSProperties } from 'react';
import type { DashboardSources, DataSource, SourceCause, SourceEta } from '../../api/dashboard';
import { TooltipProvider } from '../ui/tooltip';
import BreakdownTooltip, { type BreakdownRow } from './BreakdownTooltip';
import { fmtDay, fmtDuration, fmtEtDateTime, fmtEtDay, fmtEtTime, fmtInt } from '../../utils/format';

// Data sources (BOT-451, v2.1 design): per scraper lane, its status, the data
// maintenance pass since the cut-off and its ETA. The API sends rows problems
// first and only labels; this component turns state + cause codes into words.
// Causes are generic on purpose — a failed sign-in leaves no reason in the
// database, and a scraper always retries, so a stop never says "not running
// again". Definitions: decisions/sources.md.
//
// decision: sources — see decisions/sources.md

const STALE_AFTER_MS = 30 * 60 * 1000;
const FILE_GRACE_DAYS = 3;

type Tone = 'running' | 'waiting' | 'paused' | 'stopped' | 'off';

const TONES: Record<Tone, { badge: CSSProperties; dot: string }> = {
  running: { badge: { background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }, dot: '#22c55e' },
  waiting: { badge: { background: '#f4f4f5', borderColor: '#e4e4e7', color: '#3f3f46' }, dot: '#71717a' },
  paused: { badge: { background: '#fffbeb', borderColor: '#fde68a', color: '#92400e' }, dot: '#f59e0b' },
  stopped: { badge: { background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }, dot: '#dc2626' },
  off: { badge: { background: '#ffffff', borderColor: '#e4e4e7', color: '#52525b' }, dot: '#a1a1aa' },
};

const STOP_CAUSE: Partial<Record<SourceCause, string>> = {
  site_timeout: 'Vendor site timing out · retrying',
  bot_protection: 'Blocked by bot protection · retrying',
  errors: 'Every recent check failed · retrying',
  cant_sign_in: "Can't sign in · retrying",
  not_responding: 'Not responding',
  missed_run: 'The last scheduled run checked nothing',
};

const FEEDS: Record<string, string> = { price: 'Price', home_value: 'Home Value' };

// The hover card over Data maintenance: what the checked items' latest result
// was. The bar stays one green — every result counts as checked.
const STATUS_ROWS: { key: keyof DataSource['pass']['by_status']; label: string; color: string }[] = [
  { key: 'ok', label: 'OK', color: '#16a34a' },
  { key: 'not_found', label: 'Not found', color: '#a1a1aa' },
  { key: 'error', label: 'Error', color: '#dc2626' },
  { key: 'no_data', label: 'No data returned', color: '#e4e4e7' },
];

function statusRows(s: DataSource): BreakdownRow[] {
  return STATUS_ROWS
    .map(r => ({ key: r.key, label: r.label, dot: { backgroundColor: r.color }, value: s.pass.by_status[r.key] }))
    .filter(r => r.value > 0);
}

function addDays(isoDay: string, days: number): string {
  const d = new Date(`${isoDay}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The badge (tone + words) and the line under it. */
export function describeStatus(s: DataSource): { tone: Tone; badge: string; line: string | null } {
  const { state, cause, since, resumes_at, cdk_file_date, next_file_date, next_run_at, last_result_at } = s.status;
  const file = cdk_file_date ? fmtDay(cdk_file_date) : null;
  switch (state) {
    case 'running':
    case 'processing_file':
      return {
        tone: 'running',
        badge: state === 'processing_file' ? `Processing price file${file ? ` from ${file}` : ''}` : 'Running',
        line: lastStopLine(s),
      };
    case 'paused':
      return {
        tone: 'paused',
        badge: cause === 'login_limit'
          ? `Daily sign-in limit reached · resumes ${fmtEtTime(resumes_at)}`
          : `Paused · resumes by ${fmtEtTime(resumes_at)}`,
        line: cause === 'login_limit' ? null : 'Paused automatically or by an admin',
      };
    case 'stopped':
      return {
        tone: 'stopped',
        badge: since ? `Stopped since ${fmtEtDateTime(since)}` : 'Stopped',
        line: (cause && STOP_CAUSE[cause]) ?? null,
      };
    case 'blocked':
      return { tone: 'stopped', badge: 'Sign-in failed · needs a person', line: null };
    case 'idle':
      if (cause === 'daily_limit') {
        return { tone: 'waiting', badge: `Daily limit reached · resumes ${fmtEtTime(resumes_at)}`, line: lastStopLine(s) };
      }
      return {
        tone: 'waiting',
        badge: 'Idle',
        line: cause === 'pass_completed'
          ? `Every item checked since ${s.pass.since ? fmtDay(s.pass.since) : 'the cut-off'}`
          : 'No new work added today',
      };
    case 'waiting_file':
      if (cause === 'file_overdue' && next_file_date) {
        return {
          tone: 'paused',
          badge: `Next file overdue since ${fmtDay(addDays(next_file_date, FILE_GRACE_DAYS))}`,
          line: file ? `Last price file: ${file}` : null,
        };
      }
      return {
        tone: 'waiting',
        badge: file ? `Last price file: ${file}` : 'Waiting for a price file',
        line: next_file_date ? `Next expected ${fmtDay(next_file_date)}` : null,
      };
    case 'scheduled':
      return {
        tone: 'waiting',
        badge: `Next run ${fmtEtDateTime(next_run_at)}`,
        line: last_result_at ? `Last run ${fmtEtDateTime(last_result_at)}` : null,
      };
    case 'turned_off':
      return { tone: 'off', badge: 'Turned off', line: since ? `Turned off ${fmtEtDay(since)}` : null };
  }
}

function lastStopLine(s: DataSource): string | null {
  const stop = s.last_stop;
  if (!stop) return null;
  if (!stop.stopped_at) return `Running again since ${fmtEtTime(stop.resumed_at)}`;
  const took = new Date(stop.resumed_at).getTime() - new Date(stop.stopped_at).getTime();
  return `Stopped ${fmtEtDateTime(stop.stopped_at)} · running again ${fmtEtTime(stop.resumed_at)} (${fmtDuration(took)})`;
}

export function describeEta(eta: SourceEta): { text: string; color: string } {
  switch (eta.kind) {
    case 'days':
      return { text: `~${eta.days} ${eta.days === 1 ? 'day' : 'days'}`, color: '#09090b' };
    case 'completed':
      return { text: eta.completed_at ? `Completed ${fmtEtDay(eta.completed_at)}` : 'Completed', color: '#166534' };
    case 'unknown_stopped':
      return { text: 'Unknown · stopped', color: '#991b1b' };
    case 'turned_off':
      return { text: 'Turned off', color: '#71717a' };
    case 'over_3_months':
      return { text: '> 3 months', color: '#09090b' };
    default:
      return { text: 'Unknown', color: '#71717a' };
  }
}

function barColor(s: DataSource): string {
  if (s.status.state === 'turned_off') return '#d4d4d8';
  if (s.status.state === 'stopped' || s.status.state === 'blocked') return '#a1a1aa';
  return '#16a34a';
}

function pctLabel(pct: number | null): string {
  if (pct == null) return '—';
  return `${Number.isInteger(pct) ? pct : Math.floor(pct)}%`;
}

const ROW = 'grid grid-cols-1 gap-x-8 gap-y-2 min-[900px]:grid-cols-[200px_minmax(0,1fr)_minmax(0,1fr)] '
  + 'min-[1200px]:grid-cols-[220px_320px_minmax(0,1fr)_180px] min-[900px]:items-center';

export default function DataSources({ data, now = new Date() }: { data: DashboardSources | null; now?: Date }) {
  return (
    <TooltipProvider delayDuration={200}>
      <section aria-labelledby="h-sources" className="flex flex-col rounded-lg border border-border bg-background px-6 pb-3">
        <div className="border-b border-border">
          <h2 id="h-sources" className="pb-3.5 pt-4 text-xs font-semibold uppercase tracking-[0.06em] text-zinc-600">
            Data sources
          </h2>
        </div>

        {data == null ? (
          <div data-testid="sources-skeleton" aria-busy="true" className="flex flex-col gap-3 py-4">
            {[0, 1, 2, 3].map(i => <div key={i} className="h-12 animate-pulse rounded bg-zinc-50" />)}
          </div>
        ) : data.sources.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-zinc-500">
            Data sources are being calculated. The first numbers appear within 10 minutes.
          </p>
        ) : (
          <>
            <div className={`${ROW} hidden border-b border-zinc-100 py-2 min-[900px]:grid`}>
              <span className="text-xs text-zinc-600">Data source</span>
              <span className="text-xs text-zinc-600">Status</span>
              <span className="text-xs text-zinc-600">Data maintenance</span>
              <span className="hidden text-right text-xs text-zinc-600 min-[1200px]:block">ETA</span>
            </div>
            {data.sources.map(s => <SourceRow key={s.label} source={s} />)}
          </>
        )}

        {data?.computed_at && <Footer computedAt={data.computed_at} now={now} />}
      </section>
    </TooltipProvider>
  );
}

function SourceRow({ source: s }: { source: DataSource }) {
  const status = describeStatus(s);
  const tone = TONES[status.tone];
  const eta = describeEta(s.eta);
  const pct = s.pass.pct ?? 0;
  return (
    <div data-testid="source-row" className={`${ROW} border-b border-zinc-100 py-3.5`}>
      <div className="flex flex-col gap-0.5">
        <span data-testid="source-label" className="text-sm font-medium text-zinc-950">{s.label}</span>
        <span className="text-xs text-zinc-500">Feeds: {s.feeds.map(f => FEEDS[f]).join(' · ')}</span>
      </div>
      <div className="flex flex-col items-start gap-1">
        <span
          data-testid="source-badge"
          className="inline-flex h-6 items-center gap-1.5 rounded-md border px-[9px] text-xs font-medium"
          style={tone.badge}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone.dot }} />
          {status.badge}
        </span>
        {status.line && <span data-testid="source-line" className="text-xs text-zinc-600">{status.line}</span>}
      </div>
      <div data-testid="source-maintenance" className="flex flex-col gap-1 tabular-nums">
        <div className="flex items-baseline gap-2">
          <span className="text-[15px] font-semibold">{pctLabel(s.pass.pct)}</span>
          <span data-testid="source-pass" className="text-xs text-zinc-600">
            {fmtInt(s.pass.done)} / {fmtInt(s.pass.total)}{s.pass.since ? ` since ${fmtDay(s.pass.since)}` : ''}
          </span>
        </div>
        <BreakdownTooltip
          title={`${s.label} · checked${s.pass.since ? ` since ${fmtDay(s.pass.since)}` : ''}`}
          rows={statusRows(s)}
          total={`${fmtInt(s.pass.done)} checked`}
        >
          {/* The card sits GAP px above the bar, so the bar is the trigger — not the
              line above it, which the card would cover. The hit area reaches 6 px
              below the 8 px bar only, keeping the cursor clear of the card. */}
          <div data-testid="source-bar-hit" className="-mb-1.5 cursor-default pb-1.5">
            <div className="h-2 overflow-hidden rounded-full bg-zinc-100">
              <div data-testid="source-bar" className="h-2" style={{ width: `${Math.min(100, pct)}%`, background: barColor(s) }} />
            </div>
          </div>
        </BreakdownTooltip>
      </div>
      <span
        data-testid="source-eta"
        className="text-[13px] font-medium min-[900px]:col-start-3 min-[1200px]:col-start-auto min-[1200px]:text-right"
        style={{ color: eta.color }}
      >
        <span className="text-xs font-normal text-zinc-500 min-[1200px]:hidden">ETA </span>
        {eta.text}
      </span>
    </div>
  );
}

function Footer({ computedAt, now }: { computedAt: string; now: Date }) {
  const stale = now.getTime() - new Date(computedAt).getTime() > STALE_AFTER_MS;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-xs text-zinc-500">
      <span>As of {fmtEtDateTime(computedAt)} · refreshed every 10 minutes</span>
      {stale && <span className="text-amber-700">Not updated for over 30 minutes</span>}
    </div>
  );
}
